import * as AF from "@/components/form-items/AbstractForm";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getMainLanguage,
  isSupportedLocale,
  supportedLocales,
  toSupportedLocale,
} from "@/i18n";
import { cn } from "@/lib/utils";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const supportedLocalesTitles = [...supportedLocales, "de", "it"] as const;
type TitleLocale = (typeof supportedLocalesTitles)[number];

/**
 * Menu déroulant compact (libellé + chevron) au-dessus d’un seul champ texte,
 * comme sur la maquette fiche œuvre.
 */
export function MultilingualTitleAccordion({
  className,
  corner,
  name,
}: {
  className?: string;
  corner?: () => ReactNode;
  name: string;
}) {
  const { t, i18n } = useTranslation();
  const main = getMainLanguage();
  const [locale, setLocale] = useState<TitleLocale>(() =>
    isSupportedLocale(main) ? main : supportedLocalesTitles[0],
  );

  useEffect(() => {
    const next = toSupportedLocale(i18n.language);
    if ((supportedLocalesTitles as readonly string[]).includes(next)) {
      setLocale(next as TitleLocale);
    }
  }, [i18n.language]);

  const optionLabel = (loc: TitleLocale) =>
    t(`artwork.form.titleInLanguage.${loc}`);

  const languageSelect = (
    <Select value={locale} onValueChange={(v) => setLocale(v as TitleLocale)}>
      <SelectTrigger
        className={cn(
          "h-auto w-fit min-w-0 border-0 bg-transparent p-0 shadow-none",
          "text-sm font-semibold text-slate-700 font-['Raleway']",
          "hover:bg-transparent focus:ring-0 focus:ring-offset-0",
          "gap-1 [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:opacity-70",
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="start">
        {supportedLocalesTitles.map((loc) => (
          <SelectItem key={loc} value={loc}>
            {optionLabel(loc)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className={cn("flex w-full flex-col", className)}>
      <AF.Text
        key={locale}
        name={`${name}.${locale}`}
        label={languageSelect}
        aria-label={optionLabel(locale)}
        corner={corner}
      />
    </div>
  );
}
